import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { Building2, Copy, CheckCircle2 } from 'lucide-react';

interface PaymentInstructionsProps {
  planId?: string;
  groupId?: string;
  upgrade?: boolean;
}

const PaymentInstructions = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { planId, groupId, upgrade } = (location.state as PaymentInstructionsProps) || {};

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [plan, setPlan] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const isSuperAdmin = userProfile?.role === 'super_admin';

  useEffect(() => {
    if (planId) {
      fetchPlanDetails();
    }
  }, [planId]);

  const fetchPlanDetails = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('id', planId)
        .single();

      if (fetchError) throw fetchError;
      setPlan(data);
    } catch (err: any) {
      console.error('Error fetching plan:', err);
      setError(err.message || 'Failed to load plan details');
    } finally {
      setLoading(false);
    }
  };

  const bankDetails = {
    bankName: 'BRED Bank',
    accountName: 'Innovatelhub Solutions Ltd',
    accountNumber: '013307727100019',
    swiftCode: 'BREDVUVUXXX',
  };

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  };

  const generatePaymentReference = () => {
    // Generate reference: CP-YYYY-MMDD-HHMMSS
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `CP-${year}-${month}${day}-${hours}${minutes}${seconds}`;
  };

  const handlePaymentConfirmation = async () => {
    if (!user || !planId || !groupId) {
      setError('Missing required information');
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      // Get plan details if not already loaded
      let planData = plan;
      if (!planData) {
        const { data, error: planError } = await supabase
          .from('subscription_plans')
          .select('price_vatu')
          .eq('id', planId)
          .single();

        if (planError) throw planError;
        planData = data;
      }

      // Get or create subscription
      let subscriptionId: string | null = null;
      const { data: existingSub } = await supabase
        .from('user_subscriptions')
        .select('id, plan_id')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (existingSub) {
        subscriptionId = existingSub.id;
        // Ensure the subscription points to the plan being paid for
        if (existingSub.plan_id !== planId) {
          const { error: updateSubError } = await supabase
            .from('user_subscriptions')
            .update({ plan_id: planId, subscription_status: 'pending', updated_at: new Date().toISOString() })
            .eq('id', subscriptionId);
          if (updateSubError) throw updateSubError;
        }
      } else if (planId) {
        // Create new subscription
        const endDate = new Date();
        endDate.setDate(endDate.getDate() + 30);

        const { data: newSub, error: createSubError } = await supabase
          .from('user_subscriptions')
          .insert({
            user_id: user.id,
            plan_id: planId,
            subscription_status: 'pending',
            access_locked: false,
            start_date: new Date().toISOString(),
            end_date: endDate.toISOString(),
          })
          .select('id')
          .single();

        if (createSubError) throw createSubError;
        subscriptionId = newSub.id;
      }

      // Compute amount due for this group + plan (supports per-member pricing)
      const { data: computedAmount, error: amountError } = await supabase.rpc(
        'calculate_group_subscription_amount',
        { group_id_param: groupId, plan_id_param: planId }
      );
      if (amountError) throw amountError;
      const amountVatu = Number(computedAmount ?? planData.price_vatu ?? 0);

      // Create payment transaction (matching mobile app)
      const paymentReference = generatePaymentReference();
      const { data: paymentTransaction, error: paymentError } = await supabase
        .from('payment_transactions')
        .insert({
          user_id: user.id,
          subscription_id: subscriptionId,
          group_id: groupId,
          amount_vatu: amountVatu, // DB trigger may overwrite to ensure correctness
          payment_method: 'bank_transfer',
          payment_reference: paymentReference,
          payment_status: 'pending',
          submitted_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (paymentError) throw paymentError;

      // Create notification for super admin
      try {
        const { data: superAdmins, error: superAdminError } = await supabase
          .from('user_profiles')
          .select('id, full_name')
          .eq('role', 'super_admin');

        if (superAdminError) {
          console.error('Error fetching super admins:', superAdminError);
        } else if (superAdmins && superAdmins.length > 0) {
          const notificationPromises = superAdmins.map(async (admin) => {
            const { error } = await supabase
              .from('notifications')
              .insert({
                user_id: admin.id,
                type: 'payment_submitted',
                title: 'New Payment Submitted',
                message: `Payment of ${amountVatu.toLocaleString()} VT submitted by ${userProfile?.full_name || 'User'}. Reference: ${paymentReference}`,
                related_entity_id: paymentTransaction.id,
                is_read: false,
              });

            if (error) {
              console.error(`Error creating notification for super admin ${admin.id}:`, error);
            }
          });

          await Promise.all(notificationPromises);
        }
      } catch (notifError) {
        console.error('Error creating super admin notifications:', notifError);
      }

      // Create notification for group admin
      await supabase.from('notifications').insert({
        user_id: user.id,
        type: 'payment_pending',
        title: 'Payment Submitted',
        message: `Your payment of ${amountVatu.toLocaleString()} VT has been submitted. Reference: ${paymentReference}. Please upload your receipt on the payment status page.`,
        related_entity_id: paymentTransaction.id,
        is_read: false,
      });

      // Navigate to payment status page (matching mobile app flow)
      navigate('/payment-status', {
        state: { paymentId: paymentTransaction.id },
      });
    } catch (err: any) {
      console.error('Error submitting payment:', err);
      setError(err.message || 'Failed to submit payment. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading payment instructions...</div>
      </div>
    );
  }

  const planPrice = plan?.price_vatu || 0;

  return (
    <div className="min-h-screen bg-background">
      <Header
        onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        isMobileMenuOpen={isMobileMenuOpen}
      />

      <div className="flex">
        <div className="hidden lg:block">
          <Sidebar
            isCollapsed={isSidebarCollapsed}
            userRole={isSuperAdmin ? 'admin' : 'admin'}
            isSuperAdmin={isSuperAdmin}
          />
        </div>

        <main className={`flex-1 transition-layout ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
          <div className="p-6 max-w-4xl mx-auto">
            <div className="mb-6">
              <BreadcrumbTrail />
            </div>

            <div className="mb-8">
              <h1 className="text-3xl font-semibold text-foreground mb-2">
                Payment Instructions
              </h1>
              <p className="text-muted-foreground">
                Complete your payment via BRED Bank and upload your receipt
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-md">
                <p className="text-destructive text-sm">{error}</p>
              </div>
            )}

            {/* Payment Amount */}
            <div className="mb-6 p-6 bg-primary/10 border border-primary/20 rounded-lg">
              <p className="text-sm text-muted-foreground mb-2">Amount to Transfer</p>
              <p className="text-3xl font-bold text-primary">
                {planPrice.toLocaleString()} VT
              </p>
              {plan && (
                <p className="text-sm text-muted-foreground mt-1">
                  {plan.plan_name} - {plan.plan_type}
                </p>
              )}
            </div>

            {/* Bank Details */}
            <div className="mb-6 bg-card rounded-lg border border-border shadow-elevation-1 p-6">
              <div className="flex items-center gap-3 mb-6">
                <Building2 className="w-6 h-6 text-primary" />
                <h2 className="text-xl font-semibold text-foreground">BRED Bank Transfer Details</h2>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-border">
                  <span className="text-muted-foreground font-medium">Bank Name</span>
                  <span className="text-foreground font-semibold">{bankDetails.bankName}</span>
                </div>

                <div className="flex justify-between items-center py-3 border-b border-border">
                  <span className="text-muted-foreground font-medium">Account Name</span>
                  <div className="flex items-center gap-2">
                    <span className="text-foreground font-semibold">{bankDetails.accountName}</span>
                    <button
                      onClick={() => handleCopy(bankDetails.accountName, 'accountName')}
                      className="p-1 hover:bg-muted rounded transition-colors"
                      title="Copy account name"
                    >
                      {copied === 'accountName' ? (
                        <CheckCircle2 className="w-4 h-4 text-success" />
                      ) : (
                        <Copy className="w-4 h-4 text-muted-foreground" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex justify-between items-center py-3 border-b border-border">
                  <span className="text-muted-foreground font-medium">Account Number</span>
                  <div className="flex items-center gap-2">
                    <span className="text-foreground font-semibold font-mono">
                      {bankDetails.accountNumber}
                    </span>
                    <button
                      onClick={() => handleCopy(bankDetails.accountNumber, 'accountNumber')}
                      className="p-1 hover:bg-muted rounded transition-colors"
                      title="Copy account number"
                    >
                      {copied === 'accountNumber' ? (
                        <CheckCircle2 className="w-4 h-4 text-success" />
                      ) : (
                        <Copy className="w-4 h-4 text-muted-foreground" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex justify-between items-center py-3">
                  <span className="text-muted-foreground font-medium">SWIFT Code</span>
                  <div className="flex items-center gap-2">
                    <span className="text-foreground font-semibold font-mono">
                      {bankDetails.swiftCode}
                    </span>
                    <button
                      onClick={() => handleCopy(bankDetails.swiftCode, 'swiftCode')}
                      className="p-1 hover:bg-muted rounded transition-colors"
                      title="Copy SWIFT code"
                    >
                      {copied === 'swiftCode' ? (
                        <CheckCircle2 className="w-4 h-4 text-success" />
                      ) : (
                        <Copy className="w-4 h-4 text-muted-foreground" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Instructions */}
            <div className="mb-6 bg-card rounded-lg border border-border shadow-elevation-1 p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Payment Steps</h3>
              <ol className="space-y-3 text-sm text-muted-foreground">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary font-semibold flex items-center justify-center">1</span>
                  <span>Visit your nearest BRED Bank branch or use the BRED Bank mobile app</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary font-semibold flex items-center justify-center">2</span>
                  <span>Transfer the exact amount ({planPrice.toLocaleString()} VT) to the account details above</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary font-semibold flex items-center justify-center">3</span>
                  <span>Note the bank reference number from your transaction receipt</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary font-semibold flex items-center justify-center">4</span>
                  <span>Take a clear photo or scan of your payment receipt</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary font-semibold flex items-center justify-center">5</span>
                  <span>Click "I've Made Payment" below, then upload your receipt on the next screen</span>
                </li>
              </ol>
            </div>

            {/* Action Buttons */}
            <div className="bg-card rounded-lg border border-border shadow-elevation-1 p-6">
              <div className="space-y-3">
                <Button
                  type="button"
                  variant="default"
                  onClick={handlePaymentConfirmation}
                  loading={processing}
                  disabled={processing}
                  iconName="CheckCircle"
                  iconPosition="right"
                  fullWidth
                  size="lg"
                >
                  I've Made Payment
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/subscription-management')}
                  disabled={processing}
                  fullWidth
                >
                  Pay Later
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-4 text-center">
                After making the payment, you'll be able to upload your receipt on the next screen
              </p>
            </div>

            {/* Important Notes */}
            <div className="mt-6 p-4 bg-warning/10 border border-warning/20 rounded-lg">
              <p className="text-sm text-warning-foreground">
                <span className="font-semibold">Important:</span> Payment verification typically takes 1-2 business days. 
                You will receive a notification once your payment is approved or rejected by an administrator.
              </p>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default PaymentInstructions;

